import { resolveTiQuotePrice } from '../../calculatedAttributes/providers/tiQuote';
import { ATTR_NAMES } from '../../types/attrNames';
import {
  parseMeasureNumber,
  resolveMinimumIncomeValue,
  type RankableAttribute,
} from '../../utils/attributeMeasure';
import { getMaxGiftPriceFromGiftList } from '../../utils/helpers/calculateRewardValue';
import { MeasureValue } from '../../utils/productSorting';
import { ProductSortOption } from './types';

/**
 * One gift the reader could claim, reduced to the numbers that rank it — the
 * price and the top-up the reader pays towards it, which is what the displayed
 * figure is net of. Structural for the same reason as the attributes: the two
 * product shapes in this package spell the surrounding offer differently.
 */
export type RankableGift = {
  gift?: {
    gift_value?: { price?: number | null; topup_amount?: number | null } | null;
  } | null;
};

/**
 * The offers `GIFT_VALUE` ranks on: exclusive, bundle and welcome, the same set
 * the reward column adds up (CMS-2070 PM decision).
 */
export type RankableOffer = {
  list_of_gifts?: readonly (RankableGift | null)[] | null;
};

export type RankableOffers = {
  exclusive_offers?: readonly (RankableOffer | null)[] | null;
  bundle_exclusive_offers?: readonly (RankableOffer | null)[] | null;
  welcome_offers?: readonly (RankableOffer | null)[] | null;
};

/**
 * The premium pair a TI quote returns for one product, which is where price and
 * discount are ranked from. Structural for the same reason as the attributes:
 * the caller resolves its own product to a quote.
 */
export type RankablePremium = {
  offeredPremium?: string | undefined;
  originalPremium?: string | undefined;
};

/**
 * A free-forever card stores `ANNUAL_FEE` blank and shows `empty_value_text`,
 * so blank means 0 (CMS-2070). Blank only: text in the number field ("First
 * year annual fee waiver") stays unreadable (FR-3.2.9).
 */
const BLANK_VALUE_READS_AS: Partial<Record<string, number>> = {
  [ATTR_NAMES.ANNUAL_FEE]: 0,
};

/**
 * A stated 0 that means "none": the CC card already renders a 0 income as "no
 * requirement" (FR-1.4.7), so the NOT_DEFINED band takes it too (CMS-2070).
 */
const ZERO_READS_AS_UNDEFINED: ReadonlySet<string> = new Set([
  ATTR_NAMES.MINIMUM_ANNUAL_INCOME,
]);

export const isZeroThatMeansNone = (
  attribute: RankableAttribute | null | undefined,
): boolean =>
  !!attribute &&
  ZERO_READS_AS_UNDEFINED.has(attribute.name?.name ?? '') &&
  parseMeasureNumber(attribute.value ?? attribute.value_start) === 0;

/**
 * The number one attribute states. Shared by the sort and the filter's
 * comparisons, so a card cannot rank as free and then fail the free band.
 * Panel-only: the product card keeps showing the attribute's own text.
 */
export const readAttributeNumber = (
  attribute: RankableAttribute | null | undefined,
): MeasureValue => {
  const raw = attribute?.value ?? attribute?.value_start;
  if (attribute && (raw == null || (typeof raw === 'string' && !raw.trim()))) {
    return BLANK_VALUE_READS_AS[attribute.name?.name ?? ''] ?? null;
  }
  return parseMeasureNumber(raw);
};

/**
 * What the reader would actually get, which is what "best welcome gift" ranks on.
 *
 * Computed through the **same helper the reward figure is rendered with**
 * (`getMaxGiftPriceFromGiftList`), summed per offer exactly as
 * `calculateOffersValue` does: the best gift in each offer, net of its top-up,
 * added across offers. Ranking on the raw `price` instead put a card showing
 * HK$300 above one showing HK$800, because a 1,200 top-up never reached the
 * comparison.
 *
 * Welcome offers count too: the column shows "MoneyHero reward + welcome
 * offer", and the PM asked the sort to follow that figure (CMS-2070 § 4),
 * narrowing BRD-03 § 3.1's exclusive-only scope.
 *
 * A card with no offer at all stays missing rather than ranking 0, so it
 * sorts last in either direction.
 */
export const resolveGiftValue = (
  offers: RankableOffers | null | undefined,
): MeasureValue => {
  const list = [
    ...(offers?.exclusive_offers ?? []),
    ...(offers?.bundle_exclusive_offers ?? []),
    ...(offers?.welcome_offers ?? []),
  ].filter(Boolean);

  if (!list.length) return null;

  return list.reduce(
    (total, offer) =>
      total +
      // Structural here, `GiftListItem[]` there: the two shapes agree on the
      // fields the helper reads, and sharing it is the whole point.
      getMaxGiftPriceFromGiftList(
        (offer?.list_of_gifts ?? []) as Parameters<
          typeof getMaxGiftPriceFromGiftList
        >[0],
      ),
    0,
  );
};

/** `undefined` unless the option ranks on an attribute. */
export const getMeasureAttributeName = (
  option: ProductSortOption | undefined,
): string | undefined =>
  option?.rankBy === 'ATTRIBUTE' ? option.attributeName : undefined;

/**
 * What the reader pays, from the quote. A product the quote did not price is
 * missing rather than free, for the same reason unparseable is not 0.
 */
export const resolvePremiumValue = (
  premium: RankablePremium | null | undefined,
): MeasureValue => parseMeasureNumber(premium?.offeredPremium);

/**
 * The whole-percent discount the TI badge shows, read from the resolver the
 * table and the plan tiers use, so two plans showing the same figure tie
 * (CMS-2088).
 *
 * A priced quote that shows no discount is 0: the one group that follows every
 * discounted plan. An unpriced quote has no discount to compare, which is
 * missing rather than 0: it follows that group.
 */
export const resolveDiscountValue = (
  premium: RankablePremium | null | undefined,
): MeasureValue => {
  if (!premium) return null;

  const price = resolveTiQuotePrice(premium);
  return price ? (price.discountPercent ?? 0) : null;
};

/**
 * What a discount ranking breaks a tie on: the discounted price, lowest first
 * (INS-6293). `undefined` for any other option, which has no such tie-break.
 */
export const createTieBreakAccessor = <T>(
  option: ProductSortOption | undefined,
  getPremium?: (product: T) => RankablePremium | null | undefined,
): ((product: T) => MeasureValue) | undefined => {
  if (option?.rankBy !== 'DISCOUNT' || !getPremium) return undefined;
  return (product: T) => resolvePremiumValue(getPremium(product));
};

/**
 * `undefined` when the option ranks on no measure, so a caller can tell that
 * apart from a measure whose every value is missing.
 *
 * `getPremium` and `getOffers` are optional because only some pages can supply
 * them; an option whose source is missing ranks on nothing and leaves the order
 * untouched, which is the same outcome as a measure no product carries.
 */
export const createMeasureAccessor = <T>(
  option: ProductSortOption | undefined,
  getAttributes: (
    product: T,
  ) => readonly RankableAttribute[] | null | undefined,
  getPremium?: (product: T) => RankablePremium | null | undefined,
  getOffers?: (product: T) => RankableOffers | null | undefined,
): ((product: T) => MeasureValue) | undefined => {
  if (!option || option.rankBy === 'AUTHORED_ORDER') return undefined;

  if (option.rankBy === 'PREMIUM') {
    if (!getPremium) return undefined;
    return (product: T) => resolvePremiumValue(getPremium(product));
  }

  if (option.rankBy === 'DISCOUNT') {
    if (!getPremium) return undefined;
    return (product: T) => resolveDiscountValue(getPremium(product));
  }

  if (option.rankBy === 'MINIMUM_INCOME') {
    // The registry names the attribute, as an `ATTRIBUTE` option does: the
    // gate on `availableMeasures` reads that name, so a second copy here
    // could disagree with what the option was gated on.
    const incomeAttribute = option.attributeName;
    if (!incomeAttribute) return undefined;
    return (product: T) =>
      resolveMinimumIncomeValue(getAttributes(product), incomeAttribute);
  }

  if (option.rankBy === 'GIFT_VALUE') {
    if (!getOffers) return undefined;
    return (product: T) => resolveGiftValue(getOffers(product));
  }

  const attributeName = getMeasureAttributeName(option);
  if (!attributeName) return undefined;

  return (product: T) =>
    readAttributeNumber(
      getAttributes(product)?.find((a) => a?.name?.name === attributeName),
    );
};
